import { AddIcon, ResetIcon } from "@rothoblaas/design-system";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import Icon from "../../../components/ui/Icon";
import useI18n from "../../../hooks/useTranslations";
import styles from "./CartesianPlane.module.css";
import PlaneCoordinatesReadout from "./PlaneCoordinatesReadout";
import { createPlanePointStore, type PlanePointStore } from "./planePointStore";

// Pixels per plane unit (meter) at 100% zoom.
export const DEFAULT_GRID_STEP_PX = 40;
const MIN_GRID_STEP_PX = 12;
const MAX_GRID_STEP_PX = 160;
const ZOOM_FACTOR = 1.2;
const PAN_START_DISTANCE_PX = 4;
const MIDDLE_CLICK_RESET_THRESHOLD_MS = 400;
const MOUSE_BUTTON_MIDDLE = 1;

export type PlaneCoordinate = {
  x: number;
  y: number;
};

export type PlanePoint = PlaneCoordinate & {
  pixelX: number;
  pixelY: number;
};

export type PlaneScreenPoint = PlanePoint & {
  index: number;
};

export type PlaneBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type ToScreenPoint = (coordinate: PlaneCoordinate, index?: number) => PlaneScreenPoint | null;

type PanState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startOrigin: PlaneCoordinate;
  latestOrigin: PlaneCoordinate;
  didPan: boolean;
};

type PendingWheelZoom = {
  clientX: number;
  clientY: number;
  // One entry per wheel event, in order: 1 zooms in, -1 zooms out.
  steps: number[];
};

type CartesianPlaneRenderProps = {
  isPanning: boolean;
  // The hover point, for the layers that follow the pointer (see usePlanePoint).
  pointStore: PlanePointStore;
  // Both stable: toScreenPoint only changes with the view (origin/zoom), toPlanePointFromClient never.
  toPlanePointFromClient: (clientX: number, clientY: number) => PlanePoint | null;
  toScreenPoint: ToScreenPoint;
};

type CartesianPlaneProps = {
  children?: (props: CartesianPlaneRenderProps) => ReactNode;
  onPointClick?: (point: PlanePoint) => void;
  // Lets the owner read the hover point outside the render prop too (e.g. from a keyboard handler);
  // the plane creates its own otherwise.
  pointStore?: PlanePointStore;
  toolbarContent?: ReactNode;
  // Exposes the plane content element only (no toolbar/controls), e.g. for exporting a clean snapshot of the drawing.
  contentRef?: RefObject<HTMLDivElement | null>;
  // Hides the grid background, axis lines and origin dot for a clean export snapshot.
  exportMode?: boolean;
  // When set, the plane centers and zooms on mount so these bounds are fully visible (with padding),
  // instead of the default center/100% view. Since the plane fully remounts on every preview switch,
  // this only needs to apply once at mount time - no imperative re-fit/restore is needed.
  fitBounds?: PlaneBounds | null;
};

// Exported so consumers (e.g. the PDF export, which needs to know what fraction of a captured
// export-mode image is actually the shape vs. this margin) stay in sync with the fit computation.
export const FIT_BOUNDS_PADDING_RATIO = 0.15;

// A deck that's extremely long and narrow (e.g. 0.3m x 24m) fit strictly to its own true aspect ratio
// ends up, once placed on an A4 page, with its short axis only a couple of millimetres wide - too
// narrow for any label to sit alongside without swallowing the drawing (the actual bug behind
// "cropped"-looking exports: it's not the raster clipping, it's that nothing can fit next to it at
// that scale). Never letting the *frame* used for fitting/positioning go narrower than this ratio
// keeps the deck itself rendered at its true, undistorted scale - only the surrounding blank margin
// widens - while guaranteeing real room for labels regardless of how extreme the deck's proportions
// are. Applied to both the live/export fit (computeFitView, planeStyle) and the PDF's independent
// label positioning (computeNormalizedPosition), so the two always agree on where things land.
const MIN_FIT_ASPECT_RATIO = 0.2;

export const getEffectiveFitBounds = (bounds: PlaneBounds): PlaneBounds => {
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;

  if (width <= 0 || height <= 0) {
    return bounds;
  }

  const ratio = width / height;
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  if (ratio < MIN_FIT_ASPECT_RATIO) {
    const targetWidth = height * MIN_FIT_ASPECT_RATIO;
    return { minX: centerX - targetWidth / 2, maxX: centerX + targetWidth / 2, minY: bounds.minY, maxY: bounds.maxY };
  }

  if (ratio > 1 / MIN_FIT_ASPECT_RATIO) {
    const targetHeight = width * MIN_FIT_ASPECT_RATIO;
    return { minX: bounds.minX, maxX: bounds.maxX, minY: centerY - targetHeight / 2, maxY: centerY + targetHeight / 2 };
  }

  return bounds;
};

// Where a plane point ends up as a fraction (0-1) of the *fitted, padded* image - i.e. exactly what
// an export-mode capture (fitBounds + FIT_BOUNDS_PADDING_RATIO, aspect-ratio-locked container) would
// place at that point, but computed directly instead of through a real DOM measurement. This is what
// lets the PDF draw a measurement's text separately from the raster (as crisp vector text at the
// same size as the report's tables, added after the image is placed) while still landing it exactly
// next to the line/edge it describes - the image itself carries no baked-in text, so there's nothing
// for the plane's overflow:hidden to clip regardless of how far a label's anchor sits from the shape.
export const computeNormalizedPosition = (
  point: PlaneCoordinate,
  bounds: PlaneBounds,
  paddingRatio: number,
): { fx: number; fy: number } => {
  const shapeWidth = bounds.maxX - bounds.minX;
  const shapeHeight = bounds.maxY - bounds.minY;

  if (shapeWidth <= 0 || shapeHeight <= 0) {
    return { fx: 0.5, fy: 0.5 };
  }

  const paddedWidth = shapeWidth * (1 + paddingRatio * 2);
  const paddedHeight = shapeHeight * (1 + paddingRatio * 2);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    fx: 0.5 + (point.x - centerX) / paddedWidth,
    fy: 0.5 - (point.y - centerY) / paddedHeight,
  };
};

const computeFitView = (
  rect: { width: number; height: number },
  bounds: PlaneBounds,
  paddingRatio: number,
  // Export captures (see computeNormalizedPosition) assume the padded shape always exactly fills
  // the frame - MIN/MAX_GRID_STEP_PX exist to keep the *interactive* zoom range sane, but clamping
  // an export capture leaves extra unaccounted margin computeNormalizedPosition doesn't know about,
  // throwing off every label's fx/fy by the same amount the clamp shrank the shape.
  unclamped = false,
): { origin: PlaneCoordinate; gridStep: number } | null => {
  const shapeWidth = bounds.maxX - bounds.minX;
  const shapeHeight = bounds.maxY - bounds.minY;

  if (shapeWidth <= 0 || shapeHeight <= 0) {
    return null;
  }

  const paddedWidth = shapeWidth * (1 + paddingRatio * 2);
  const paddedHeight = shapeHeight * (1 + paddingRatio * 2);
  const rawGridStep = Math.min(rect.width / paddedWidth, rect.height / paddedHeight);
  const gridStep = unclamped ? rawGridStep : clamp(rawGridStep, MIN_GRID_STEP_PX, MAX_GRID_STEP_PX);

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    gridStep,
    origin: {
      x: rect.width / 2 - centerX * gridStep,
      y: rect.height / 2 + centerY * gridStep,
    },
  };
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const roundCoordinate = (value: number): number => Math.round(value * 100) / 100;

const getPlaneCenter = (rect: { width: number; height: number }): PlaneCoordinate => ({
  x: rect.width / 2,
  y: rect.height / 2,
});

const toPlanePoint = (
  pixelX: number,
  pixelY: number,
  planeOrigin: PlaneCoordinate,
  planeGridStep: number,
): PlanePoint => ({
  pixelX,
  pixelY,
  x: roundCoordinate((pixelX - planeOrigin.x) / planeGridStep),
  y: roundCoordinate((planeOrigin.y - pixelY) / planeGridStep),
});

const toTranslate = (from: PlaneCoordinate, to: PlaneCoordinate): string =>
  `translate(${to.x - from.x}px, ${to.y - from.y}px)`;

const CartesianPlane: FC<CartesianPlaneProps> = ({
  children,
  onPointClick,
  pointStore: externalPointStore,
  toolbarContent,
  contentRef,
  exportMode,
  fitBounds,
}) => {
  const i18n = useI18n();
  const commonTranslation = i18n.translation.common;
  const axisIndicatorArrowId = `${useId()}-axis-indicator-arrow`;
  const planeRef = useRef<HTMLDivElement>(null);
  const contentLayerRef = useRef<HTMLDivElement>(null);
  const planeRectRef = useRef<DOMRect | null>(null);
  const panStateRef = useRef<PanState | null>(null);
  const lastMiddleClickTimeRef = useRef(0);
  const pendingWheelZoomRef = useRef<PendingWheelZoom | null>(null);
  const wheelZoomFrameRef = useRef(0);
  const [ownPointStore] = useState(createPlanePointStore);
  const pointStore = externalPointStore ?? ownPointStore;
  const [origin, setOrigin] = useState<PlaneCoordinate | null>(null);
  const [gridStep, setGridStep] = useState(DEFAULT_GRID_STEP_PX);
  const [isPanning, setIsPanning] = useState(false);
  // The committed view, for the stable toPlanePointFromClient - synced after every commit.
  const originRef = useRef<PlaneCoordinate | null>(null);
  const gridStepRef = useRef(DEFAULT_GRID_STEP_PX);
  const effectiveFitBounds = useMemo(() => (fitBounds ? getEffectiveFitBounds(fitBounds) : null), [fitBounds]);

  // Cached instead of measured on every pointer move: cleared whenever the plane may have moved or
  // resized (see the effect below), on every press - also one a child stops, e.g. a drag handle - and
  // whenever the pointer comes back in.
  const clearPlaneRect = useCallback(() => {
    planeRectRef.current = null;
  }, []);

  const getPlaneRect = useCallback((): DOMRect | null => {
    const plane = planeRef.current;

    if (!plane) {
      return null;
    }

    planeRectRef.current ??= plane.getBoundingClientRect();

    return planeRectRef.current;
  }, []);

  // While panning, the origin the content is previewed at (not committed yet, see previewPan).
  const getLiveOrigin = useCallback(
    (rect: DOMRect): PlaneCoordinate => panStateRef.current?.latestOrigin ?? originRef.current ?? getPlaneCenter(rect),
    [],
  );

  const getPointFromClient = useCallback(
    (clientX: number, clientY: number, planeOrigin?: PlaneCoordinate): PlanePoint | null => {
      const rect = getPlaneRect();

      if (!rect) {
        return null;
      }

      return toPlanePoint(
        clamp(clientX - rect.left, 0, rect.width),
        clamp(clientY - rect.top, 0, rect.height),
        planeOrigin ?? getLiveOrigin(rect),
        gridStepRef.current,
      );
    },
    [getLiveOrigin, getPlaneRect],
  );

  const toPlanePointFromClient = useCallback(
    (clientX: number, clientY: number): PlanePoint | null => getPointFromClient(clientX, clientY),
    [getPointFromClient],
  );

  const toScreenPoint = useCallback<ToScreenPoint>(
    (coordinate, index = 0) =>
      origin
        ? {
            ...coordinate,
            index,
            pixelX: origin.x + coordinate.x * gridStep,
            pixelY: origin.y - coordinate.y * gridStep,
          }
        : null,
    [gridStep, origin],
  );

  const updatePoint = (event: PointerEvent<HTMLDivElement>, planeOrigin?: PlaneCoordinate): PlanePoint | null => {
    const nextPoint = getPointFromClient(event.clientX, event.clientY, planeOrigin);

    if (!nextPoint) {
      return null;
    }

    pointStore.set(nextPoint);

    return nextPoint;
  };

  const updatePointFromCurrentPixel = (nextOrigin: PlaneCoordinate, nextGridStep: number) => {
    const point = pointStore.getLatest();

    if (!point) {
      return;
    }

    pointStore.set(toPlanePoint(point.pixelX, point.pixelY, nextOrigin, nextGridStep));
  };

  // A drag in progress carries on from the new view, instead of jumping back to the old one on its
  // next move.
  const setView = (nextOrigin: PlaneCoordinate, nextGridStep: number, rect: DOMRect) => {
    const panState = panStateRef.current;

    if (panState) {
      const liveOrigin = getLiveOrigin(rect);

      panState.startOrigin = {
        x: panState.startOrigin.x + nextOrigin.x - liveOrigin.x,
        y: panState.startOrigin.y + nextOrigin.y - liveOrigin.y,
      };
      panState.latestOrigin = nextOrigin;
    }

    setGridStep(nextGridStep);
    setOrigin(nextOrigin);
  };

  const zoomAt = (nextGridStep: number, anchor?: PlaneCoordinate) => {
    const rect = getPlaneRect();

    if (!rect) {
      return;
    }

    const currentOrigin = getLiveOrigin(rect);
    const nextStep = clamp(nextGridStep, MIN_GRID_STEP_PX, MAX_GRID_STEP_PX);
    const point = pointStore.getLatest();
    const zoomAnchor = anchor ?? (point ? { x: point.pixelX, y: point.pixelY } : getPlaneCenter(rect));
    const zoomRatio = nextStep / gridStep;
    const nextOrigin = {
      x: zoomAnchor.x - (zoomAnchor.x - currentOrigin.x) * zoomRatio,
      y: zoomAnchor.y - (zoomAnchor.y - currentOrigin.y) * zoomRatio,
    };

    setView(nextOrigin, nextStep, rect);

    if (anchor) {
      pointStore.set(toPlanePoint(anchor.x, anchor.y, nextOrigin, nextStep));
      return;
    }

    updatePointFromCurrentPixel(nextOrigin, nextStep);
  };

  const resetView = () => {
    const plane = planeRef.current;

    if (!plane) {
      return;
    }

    const rect = plane.getBoundingClientRect();
    const nextOrigin = getPlaneCenter(rect);

    setView(nextOrigin, DEFAULT_GRID_STEP_PX, rect);
    updatePointFromCurrentPixel(nextOrigin, DEFAULT_GRID_STEP_PX);
  };

  const getInitialView = (rect: DOMRect): { origin: PlaneCoordinate; gridStep: number } => {
    const fittedView = effectiveFitBounds
      ? computeFitView(rect, effectiveFitBounds, FIT_BOUNDS_PADDING_RATIO, exportMode)
      : null;

    return fittedView ?? { origin: getPlaneCenter(rect), gridStep: DEFAULT_GRID_STEP_PX };
  };

  // Declared first, so the refs are already in sync when the effects below run.
  useLayoutEffect(() => {
    originRef.current = origin;
    gridStepRef.current = gridStep;

    // A commit in the middle of a pan (e.g. a wheel zoom) keeps previewing it from the new origin.
    const content = contentLayerRef.current;
    const panState = panStateRef.current;

    if (content) {
      content.style.transform = panState?.didPan && origin ? toTranslate(origin, panState.latestOrigin) : "";
    }
  }, [gridStep, origin]);

  useLayoutEffect(() => {
    const plane = planeRef.current;

    if (!plane || origin) {
      return;
    }

    const rect = plane.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    const initialView = getInitialView(rect);
    setGridStep(initialView.gridStep);
    setOrigin(initialView.origin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, fitBounds]);

  // The effect above only ever fits once (guarded by `origin`), which is deliberate for the
  // interactive view - it must never clobber the user's own pan/zoom just because the shape changed
  // underneath it. But an export-mode capture (see DeckDesigner's .captureStage) can still be settling
  // its shape after it mounts, so without this it could keep the fit of a half-settled shape. Export
  // mode has no user pan to preserve, so it always re-fits to whatever the shape currently is.
  useLayoutEffect(() => {
    if (!exportMode) {
      return;
    }

    const plane = planeRef.current;

    if (!plane) {
      return;
    }

    const rect = plane.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    const initialView = getInitialView(rect);
    setGridStep(initialView.gridStep);
    setOrigin(initialView.origin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportMode, effectiveFitBounds]);

  useEffect(() => {
    const plane = planeRef.current;

    if (!plane || origin) {
      return;
    }

    let animationFrame = 0;
    const syncOrigin = () => {
      const rect = plane.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      setOrigin((currentOrigin) => {
        if (currentOrigin) {
          return currentOrigin;
        }

        const initialView = getInitialView(rect);
        setGridStep(initialView.gridStep);
        return initialView.origin;
      });
    };
    const scheduleSyncOrigin = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(syncOrigin);
    };

    scheduleSyncOrigin();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleSyncOrigin();
          });
    const intersectionObserver =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(() => {
            scheduleSyncOrigin();
          });

    resizeObserver?.observe(plane);
    intersectionObserver?.observe(plane);
    window.addEventListener("resize", scheduleSyncOrigin);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener("resize", scheduleSyncOrigin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, fitBounds]);

  // The cached rect goes stale whenever the plane may have moved: on scroll (of the page or of any
  // scrolling ancestor) and on resize - its own or its wrapper's, e.g. a toolbar wrapping onto a
  // second line pushes the plane down.
  useEffect(() => {
    const plane = planeRef.current;

    if (!plane) {
      return;
    }

    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(clearPlaneRect);

    resizeObserver?.observe(plane);

    if (plane.parentElement) {
      resizeObserver?.observe(plane.parentElement);
    }

    window.addEventListener("scroll", clearPlaneRect, { capture: true, passive: true });
    window.addEventListener("resize", clearPlaneRect);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", clearPlaneRect, { capture: true });
      window.removeEventListener("resize", clearPlaneRect);
    };
  }, [clearPlaneRect]);

  // Panning moves the already rendered content (and the grid/axes, through the plane's CSS variables)
  // instead of re-rendering every layer on every pointer move - the origin is committed on release.
  const previewPan = (nextOrigin: PlaneCoordinate) => {
    const plane = planeRef.current;
    const content = contentLayerRef.current;
    const committedOrigin = originRef.current;

    if (!plane || !content || !committedOrigin) {
      return;
    }

    plane.style.setProperty("--origin-x", `${nextOrigin.x}px`);
    plane.style.setProperty("--origin-y", `${nextOrigin.y}px`);
    content.style.transform = toTranslate(committedOrigin, nextOrigin);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button === MOUSE_BUTTON_MIDDLE) {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);

      const now = Date.now();

      if (now - lastMiddleClickTimeRef.current < MIDDLE_CLICK_RESET_THRESHOLD_MS) {
        lastMiddleClickTimeRef.current = 0;
        resetView();
      } else {
        lastMiddleClickTimeRef.current = now;
      }

      return;
    }

    if (event.button !== 0) {
      return;
    }

    const rect = getPlaneRect();

    if (!rect) {
      return;
    }

    const currentOrigin = originRef.current ?? getPlaneCenter(rect);

    event.currentTarget.setPointerCapture(event.pointerId);
    panStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startOrigin: currentOrigin,
      latestOrigin: currentOrigin,
      didPan: false,
    };
    setOrigin(currentOrigin);
    setIsPanning(false);
    updatePoint(event, currentOrigin);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const panState = panStateRef.current;

    if (!panState || panState.pointerId !== event.pointerId) {
      updatePoint(event);
      return;
    }

    const deltaX = event.clientX - panState.startClientX;
    const deltaY = event.clientY - panState.startClientY;
    const hasStartedPanning = panState.didPan || Math.hypot(deltaX, deltaY) > PAN_START_DISTANCE_PX;

    if (!hasStartedPanning) {
      updatePoint(event, panState.startOrigin);
      return;
    }

    const nextOrigin = {
      x: panState.startOrigin.x + deltaX,
      y: panState.startOrigin.y + deltaY,
    };

    panState.latestOrigin = nextOrigin;

    if (!panState.didPan) {
      panState.didPan = true;
      setIsPanning(true);
    }

    previewPan(nextOrigin);
    updatePoint(event, nextOrigin);
  };

  const finishPanning = (event: PointerEvent<HTMLDivElement>, shouldEmitClick: boolean) => {
    const panState = panStateRef.current;
    const finishedOrigin = panState?.latestOrigin;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    panStateRef.current = null;
    setIsPanning(false);

    if (panState?.didPan) {
      setOrigin(panState.latestOrigin);
    }

    const releasedPoint = updatePoint(event, finishedOrigin);

    if (shouldEmitClick && panState && !panState.didPan && releasedPoint) {
      onPointClick?.(releasedPoint);
    }
  };

  const applyPendingWheelZoom = useEffectEvent(() => {
    const pendingWheelZoom = pendingWheelZoomRef.current;
    const rect = getPlaneRect();

    pendingWheelZoomRef.current = null;
    wheelZoomFrameRef.current = 0;

    if (!pendingWheelZoom || !rect) {
      return;
    }

    const anchor = {
      x: clamp(pendingWheelZoom.clientX - rect.left, 0, rect.width),
      y: clamp(pendingWheelZoom.clientY - rect.top, 0, rect.height),
    };
    // Same result as one zoomAt per wheel event: each step is clamped on its own (so in/in/out at the
    // max ends one step below it), and since all steps share the anchor their origin shifts telescope
    // into the single nextStep / gridStep ratio zoomAt applies.
    const nextStep = pendingWheelZoom.steps.reduce(
      (currentStep, step) =>
        clamp(step > 0 ? currentStep * ZOOM_FACTOR : currentStep / ZOOM_FACTOR, MIN_GRID_STEP_PX, MAX_GRID_STEP_PX),
      gridStep,
    );

    zoomAt(nextStep, anchor);
  });

  // Trackpads fire dozens of wheel events per second - they're queued and applied once per frame
  // (anchored on the latest pointer position) instead of re-rendering the plane for each of them.
  const handleWheel = useEffectEvent((event: globalThis.WheelEvent) => {
    if (!planeRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const pendingWheelZoom = pendingWheelZoomRef.current ?? { clientX: 0, clientY: 0, steps: [] };

    pendingWheelZoom.clientX = event.clientX;
    pendingWheelZoom.clientY = event.clientY;
    pendingWheelZoom.steps.push(event.deltaY < 0 ? 1 : -1);
    pendingWheelZoomRef.current = pendingWheelZoom;

    if (!wheelZoomFrameRef.current) {
      wheelZoomFrameRef.current = window.requestAnimationFrame(() => applyPendingWheelZoom());
    }
  });

  const handleMouseDown = useEffectEvent((event: globalThis.MouseEvent) => {
    if (event.button !== MOUSE_BUTTON_MIDDLE) {
      return;
    }

    const plane = planeRef.current;

    if (!plane || !(event.target instanceof Node) || !plane.contains(event.target)) {
      return;
    }

    event.preventDefault();
  });

  // Bound once: the handlers are Effect Events, so they always see the latest state without the
  // listeners being re-added on every render (the plane re-renders on every view change). The
  // handlers' identity changes per render, so add/remove must both use the ones captured here.
  useEffect(() => {
    const plane = planeRef.current;

    if (!plane) {
      return;
    }

    plane.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("mousedown", handleMouseDown, { capture: true, passive: false });

    return () => {
      plane.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mousedown", handleMouseDown, { capture: true });
      window.cancelAnimationFrame(wheelZoomFrameRef.current);
      wheelZoomFrameRef.current = 0;
      pendingWheelZoomRef.current = null;
    };
  }, []);

  const planeStyle = useMemo<CSSProperties>(() => {
    // Matching the container's aspect ratio to the fitted shape's own aspect ratio removes the
    // leftover margin computeFitView() otherwise leaves on whichever axis isn't the tightest fit
    // (min-height must be cleared too, since it would otherwise floor the aspect-ratio-derived height).
    const fitAspectRatio =
      effectiveFitBounds &&
      effectiveFitBounds.maxX > effectiveFitBounds.minX &&
      effectiveFitBounds.maxY > effectiveFitBounds.minY
        ? (effectiveFitBounds.maxX - effectiveFitBounds.minX) / (effectiveFitBounds.maxY - effectiveFitBounds.minY)
        : null;

    return {
      "--grid-step": `${gridStep}px`,
      "--origin-x": origin ? `${origin.x}px` : "50%",
      "--origin-y": origin ? `${origin.y}px` : "50%",
      ...(exportMode
        ? {
            backgroundImage: "none",
            border: "none",
            ...(fitAspectRatio ? { aspectRatio: fitAspectRatio, minHeight: 0 } : null),
          }
        : null),
    } as CSSProperties;
  }, [gridStep, origin, exportMode, effectiveFitBounds]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarInfo}>
          <PlaneCoordinatesReadout pointStore={pointStore} />
          {toolbarContent}
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => zoomAt(gridStep / ZOOM_FACTOR)}
            title={commonTranslation.zoomOut}
          >
            <span aria-hidden="true">-</span>
          </button>
          <span className={styles.zoomValue}>{Math.round((gridStep / DEFAULT_GRID_STEP_PX) * 100)}%</span>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => zoomAt(gridStep * ZOOM_FACTOR)}
            title={commonTranslation.zoomIn}
          >
            <Icon glyph={AddIcon} className={styles.icon} />
          </button>
          <button type="button" className={styles.iconButton} onClick={resetView} title={commonTranslation.resetView}>
            <Icon glyph={ResetIcon} className={styles.icon} />
          </button>
        </div>
      </div>

      <div
        ref={(node) => {
          planeRef.current = node;
          if (contentRef) {
            contentRef.current = node;
          }
        }}
        className={`${styles.plane} ${isPanning ? styles.panning : ""}`}
        onPointerDownCapture={clearPlaneRect}
        onPointerDown={handlePointerDown}
        onPointerEnter={clearPlaneRect}
        onPointerMove={handlePointerMove}
        onPointerUp={(event) => finishPanning(event, true)}
        onPointerCancel={(event) => finishPanning(event, false)}
        onPointerLeave={() => {
          if (!panStateRef.current) {
            pointStore.set(null);
          }
        }}
        style={planeStyle}
      >
        {!exportMode && (
          <>
            <div className={styles.axisX} />
            <div className={styles.axisY} />
            <div className={styles.origin} />
          </>
        )}
        <div ref={contentLayerRef} className={styles.content}>
          {children?.({ isPanning, pointStore, toPlanePointFromClient, toScreenPoint })}
        </div>

        {!exportMode && (
          <div className={styles.axisIndicator} aria-hidden="true">
            <svg viewBox="0 0 48 48">
              <defs>
                <marker id={axisIndicatorArrowId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="var(--text)" />
                </marker>
              </defs>
              <line
                x1="6"
                y1="42"
                x2="38"
                y2="42"
                stroke="var(--text)"
                strokeWidth="1.5"
                markerEnd={`url(#${axisIndicatorArrowId})`}
              />
              <line
                x1="6"
                y1="42"
                x2="6"
                y2="10"
                stroke="var(--text)"
                strokeWidth="1.5"
                markerEnd={`url(#${axisIndicatorArrowId})`}
              />
              <text x="40" y="46" fontSize="10" fill="var(--text)">
                1
              </text>
              <text x="10" y="14" fontSize="10" fill="var(--text)">
                2
              </text>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartesianPlane;
